import { createContext, useContextSelector, type MyContextType as Context } from 'react-ctx-selector';
import {PropsWithChildren, useMemo, useState} from 'react';
import type {BreadcrumbItem} from '@app/types';

type BreadcrumbsContextType = {
    list: BreadcrumbItem[]
    set: (to: BreadcrumbItem[]|(() => BreadcrumbItem[])) => void;
};

export const BreadcrumbsContext = createContext<BreadcrumbsContextType>(undefined!);
export function BreadcrumbsContextProvider({children}: PropsWithChildren) {

    const [list, setList] = useState<BreadcrumbItem[]>([]);
    const context = useMemo<BreadcrumbsContextType>(() => ({
        list,
        set: setList
    }), [list, setList]);

    return (
        <BreadcrumbsContext.Provider value={context}>
            {children}
        </BreadcrumbsContext.Provider>
    )
}

function useBreadcrumbsContext<T>(
    selector?: (value: BreadcrumbsContextType) => T
): T extends BreadcrumbsContextType ? BreadcrumbsContextType : T {
    const context = useContextSelector<BreadcrumbsContextType, T>(
        BreadcrumbsContext as unknown as Context<BreadcrumbsContextType>,
        selector ?? ((value) => value as T),
        undefined,
        () => {
            throw new Error('useBreadcrumbsContext must be used within a BreadcrumbsContextProvider');
        }
    );
    return context as T extends BreadcrumbsContextType ? BreadcrumbsContextType : T;
}

export const useBreadcrumbsList = () => {
    return useBreadcrumbsContext(c => c.list);
}

export const useSetBreadcrumbs = () => {
    return useBreadcrumbsContext(c => c.set);
}